import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AIGrid from "@/components/AIGrid";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, CreditCard, Heart, ShoppingBag, Headphones, Shield, TrendingUp, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { lazy, Suspense } from "react";
import fintechHero from "@/assets/fintech-hero-v2.jpg";
import telecomHero from "@/assets/telecom-hero-v2.jpg";
import healthcareHero from "@/assets/healthcare-hero-v2.jpg";
import ecommerceHero from "@/assets/ecommerce-hero-v2.jpg";
import bpoHero from "@/assets/bpo-hero-v2.jpg";
import { useTranslation } from 'react-i18next';

// Lazy load ROI Calculator for better performance
const ROICalculator = lazy(() => import("@/components/ROICalculator"));

const Industries = () => {
  const { t } = useTranslation();
  
  const industries = [
    {
      icon: CreditCard,
      name: t('industries.fintech_name'),
      image: fintechHero,
      challenge: t('industries.fintech_challenge'),
      solution: t('industries.fintech_solution'),
      outcome: t('industries.fintech_outcome'),
      complianceBadges: ["GDPR", "PCI-DSS", "ISO 27001"],
      beforeAfter: [
        { problem: t('industries.fintech_before_1_problem'), solution: t('industries.fintech_before_1_solution'), improvement: t('industries.fintech_before_1_improvement') },
        { problem: t('industries.fintech_before_2_problem'), solution: t('industries.fintech_before_2_solution'), improvement: t('industries.fintech_before_2_improvement') },
        { problem: t('industries.fintech_before_3_problem'), solution: t('industries.fintech_before_3_solution'), improvement: t('industries.fintech_before_3_improvement') },
      ],
    },
    {
      icon: Phone,
      name: t('industries.telecom_name'),
      image: telecomHero,
      challenge: t('industries.telecom_challenge'),
      solution: t('industries.telecom_solution'),
      outcome: t('industries.telecom_outcome'),
      complianceBadges: ["GDPR", "SOC 2"],
      beforeAfter: [
        { problem: t('industries.telecom_before_1_problem'), solution: t('industries.telecom_before_1_solution'), improvement: t('industries.telecom_before_1_improvement') },
        { problem: t('industries.telecom_before_2_problem'), solution: t('industries.telecom_before_2_solution'), improvement: t('industries.telecom_before_2_improvement') },
        { problem: t('industries.telecom_before_3_problem'), solution: t('industries.telecom_before_3_solution'), improvement: t('industries.telecom_before_3_improvement') },
      ],
    },
    {
      icon: Heart,
      name: t('industries.healthcare_name'),
      image: healthcareHero,
      challenge: t('industries.healthcare_challenge'),
      solution: t('industries.healthcare_solution'),
      outcome: t('industries.healthcare_outcome'),
      complianceBadges: ["HIPAA", "GDPR", "ISO 27001"],
      beforeAfter: [
        { problem: t('industries.healthcare_before_1_problem'), solution: t('industries.healthcare_before_1_solution'), improvement: t('industries.healthcare_before_1_improvement') },
        { problem: t('industries.healthcare_before_2_problem'), solution: t('industries.healthcare_before_2_solution'), improvement: t('industries.healthcare_before_2_improvement') },
        { problem: t('industries.healthcare_before_3_problem'), solution: t('industries.healthcare_before_3_solution'), improvement: t('industries.healthcare_before_3_improvement') },
      ],
    },
    {
      icon: ShoppingBag,
      name: t('industries.ecommerce_name'),
      image: ecommerceHero,
      challenge: t('industries.ecommerce_challenge'),
      solution: t('industries.ecommerce_solution'),
      outcome: t('industries.ecommerce_outcome'),
      complianceBadges: ["GDPR", "PCI-DSS"],
      beforeAfter: [
        { problem: t('industries.ecommerce_before_1_problem'), solution: t('industries.ecommerce_before_1_solution'), improvement: t('industries.ecommerce_before_1_improvement') },
        { problem: t('industries.ecommerce_before_2_problem'), solution: t('industries.ecommerce_before_2_solution'), improvement: t('industries.ecommerce_before_2_improvement') },
        { problem: t('industries.ecommerce_before_3_problem'), solution: t('industries.ecommerce_before_3_solution'), improvement: t('industries.ecommerce_before_3_improvement') },
      ],
    },
    {
      icon: Headphones,
      name: t('industries.bpo_name'),
      image: bpoHero,
      challenge: t('industries.bpo_challenge'),
      solution: t('industries.bpo_solution'),
      outcome: t('industries.bpo_outcome'),
      complianceBadges: ["ISO 27001", "SOC 2", "GDPR"],
      beforeAfter: [
        { problem: t('industries.bpo_before_1_problem'), solution: t('industries.bpo_before_1_solution'), improvement: t('industries.bpo_before_1_improvement') },
        { problem: t('industries.bpo_before_2_problem'), solution: t('industries.bpo_before_2_solution'), improvement: t('industries.bpo_before_2_improvement') },
        { problem: t('industries.bpo_before_3_problem'), solution: t('industries.bpo_before_3_solution'), improvement: t('industries.bpo_before_3_improvement') },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 gradient-brand-text tracking-tight">{t('industries.title')}</h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              {t('industries.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-16 max-w-7xl mx-auto">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.4 }}
              >
                <Card className="overflow-hidden glass-effect border-primary/20 hover:border-primary/40 transition-all duration-300">
                  {/* Industry Hero Image */}
                  <div className="relative h-[24rem] overflow-hidden">
                    <img 
                      src={industry.image} 
                      alt={industry.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/20" />
                    <div className="absolute bottom-8 left-8 right-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-xl">
                          <industry.icon className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{industry.name}</h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {industry.complianceBadges.map((badge, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-semibold"
                          >
                            <Shield className="w-3.5 h-3.5" />
                            {badge}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Industry Content */}
                  <div className="p-8 md:p-10 space-y-8">
                    {/* Challenge & Solution Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-red-500/5 border border-red-500/20">
                        <h3 className="text-sm font-bold text-red-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center text-xs">✕</span>
                          {t('industries.challenge_label')}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">{industry.challenge}</p>
                      </div>
                      
                      <div className="p-6 rounded-xl bg-primary/5 border border-primary/20">
                        <h3 className="text-sm font-bold text-primary uppercase tracking-wide mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          {t('industries.solution_label')}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">{industry.solution}</p>
                      </div>
                    </div>

                    {/* Before/After Comparison */}
                    <div className="pt-4">
                      <BeforeAfterSlider items={industry.beforeAfter} />
                    </div>
                    
                    {/* Business Impact */}
                    <div className="p-6 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center shrink-0">
                          <TrendingUp className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-green-400 mb-2 text-lg">{t('industries.impact_label')}</h3>
                          <p className="text-muted-foreground leading-relaxed">{industry.outcome}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* ROI Calculator - Only on first industry, lazy loaded */}
                    {index === 0 && (
                      <div className="mt-8 pt-8 border-t border-border">
                        <Suspense fallback={
                          <div className="text-center py-8 text-muted-foreground">{t('industries.loading_roi_calculator')}</div>
                        }>
                          <ROICalculator />
                        </Suspense>
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <Link to="/contact" className="flex-1">
                        <Button className="w-full gradient-brand text-white hover:opacity-90 py-6 group">
                          {t('industries.demo_button')}
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Button>
                      </Link>
                      <Link to="/solutions" className="flex-1">
                        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white py-6">
                          {t('industries.solutions_button')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4 }}
            className="mt-20 text-center"
          >
            <Card className="p-10 md:p-12 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('industries.cta_title')}</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                {t('industries.cta_subtitle')}
              </p>
              <Link to="/contact">
                <Button size="lg" className="gradient-brand text-white hover:opacity-90 group">
                  {t('industries.cta_button')}
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Industries;